import React, { useCallback, useEffect, useState } from "react";
import { MultiSelect } from "primereact/multiselect";
import AutenticacaoService from "../../services/AutenticacaoService";
import DownloadService from "../../services/DownloadService";
import RelatorioService from "../../services/RelatorioService";
import FlexSpinner from "../FlexFreteSpinner/FlexSpinner";
import Modal from "../FlexFreteModal/Modal";
import { GlobalFilterStyle } from "../MeusEnviosFreteList/style";
import {
  MultiSelectGlobalStyle,
  RelatorioEncomendasContainer,
  RelatorioFiltrosForm,
  TabelaWrapper,
} from "./style";

const relatorioService = new RelatorioService();
const downloadService = new DownloadService();
const autenticacaoService = new AutenticacaoService();

const STATUS_OPTIONS = [
  { label: "Pendente", value: "PENDENTE" },
  { label: "Gerado", value: "GERADO" },
  { label: "Postado", value: "POSTADO" },
  { label: "Entregue", value: "ENTREGUE" },
  { label: "Expirado/Carrinho Expirado", value: "EXPIRADO" },
  { label: "Cancelado", value: "CANCELADO" },
];

const INITIAL_REQUEST = {
  size: 20,
  page: 0,
  sortBy: "dataCriacao",
  sortDir: "desc",
  dataInicio: "",
  dataFim: "",
  status: [],
  codigosEncomenda: [],
  userUuid: "",
};

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString("pt-BR");
}

function formatCurrency(value) {
  if (value === null || value === undefined) return "-";
  return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function nomePessoa(pessoa) {
  if (!pessoa) return "-";
  return [pessoa.nome, pessoa.sobrenome].filter(Boolean).join(" ") || "-";
}

function SortIcon({ field, sortBy, sortDir }) {
  if (sortBy !== field) return <span className="sort-icon">⇅</span>;
  return <span className="sort-icon">{sortDir === "asc" ? "↑" : "↓"}</span>;
}

export default function RelatorioEncomendas() {
  const usuario = autenticacaoService.usuarioCorrente();
  const isAdmin =
    usuario &&
    Array.isArray(usuario.roles) &&
    usuario.roles.some((r) => r === "ADMIN" || r === "ROLE_ADMIN");

  const [request, setRequest] = useState(INITIAL_REQUEST);
  const [filtroAtivo, setFiltroAtivo] = useState(INITIAL_REQUEST);
  const [encomendas, setEncomendas] = useState([]);
  const [totalEncomendas, setTotalEncomendas] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportando, setIsExportando] = useState(false);
  const [erro, setErro] = useState({ isWrong: false, titulo: "", mensagem: "" });

  const carregarEncomendas = useCallback((filtro, append = false) => {
    setIsLoading(true);
    relatorioService.listarEncomendas(filtro, (err, data) => {
      setIsLoading(false);
      if (err) {
        setErro({
          isWrong: true,
          titulo: "Erro ao carregar relatório",
          mensagem: err.response?.data?.message || "Não foi possível carregar o relatório.",
        });
        return;
      }
      const items = data?.content ?? (Array.isArray(data) ? data : []);
      const total = data?.totalElements ?? items.length;
      setTotalEncomendas(total);
      setEncomendas((prev) => (append ? [...prev, ...items] : items));
    });
  }, []);

  useEffect(() => {
    carregarEncomendas(filtroAtivo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitForm = (e) => {
    e.preventDefault();
    const codigo = request.codigosEncomenda[0]?.trim();
    const novoFiltro = { ...request, codigosEncomenda: codigo ? [codigo] : [], page: 0 };
    setFiltroAtivo(novoFiltro);
    setEncomendas([]);
    carregarEncomendas(novoFiltro);
  };

  const carregarMais = () => {
    const proximaPagina = filtroAtivo.page + 1;
    const novoFiltro = { ...filtroAtivo, page: proximaPagina };
    setFiltroAtivo(novoFiltro);
    carregarEncomendas(novoFiltro, true);
  };

  const handleSort = (campo) => {
    const novaDir = filtroAtivo.sortBy === campo && filtroAtivo.sortDir === "asc" ? "desc" : "asc";
    const novoFiltro = { ...filtroAtivo, sortBy: campo, sortDir: novaDir, page: 0 };
    setFiltroAtivo(novoFiltro);
    setRequest((prev) => ({ ...prev, sortBy: campo, sortDir: novaDir }));
    setEncomendas([]);
    carregarEncomendas(novoFiltro);
  };

  const exportarCSV = () => {
    setIsExportando(true);
    const { dataInicio, dataFim, status, codigosEncomenda, userUuid } = filtroAtivo;
    relatorioService.exportarEncomendasCSV(
      { dataInicio, dataFim, status, codigosEncomenda, userUuid },
      (err, data) => {
        setIsExportando(false);
        if (err) {
          setErro({
            isWrong: true,
            titulo: "Erro ao exportar",
            mensagem: "Não foi possível exportar o relatório.",
          });
        } else {
          downloadService.downloadFile({ label: "relatorio_encomendas", data, mime: "text/csv" });
        }
      }
    );
  };

  const handleSizeChange = (novoSize) => {
    const novoFiltro = { ...filtroAtivo, size: novoSize, page: 0 };
    setRequest((prev) => ({ ...prev, size: novoSize }));
    setFiltroAtivo(novoFiltro);
    setEncomendas([]);
    carregarEncomendas(novoFiltro);
  };

  const thSortable = (label, campo) => (
    <th className="sortable" onClick={() => handleSort(campo)}>
      {label}
      <SortIcon field={campo} sortBy={filtroAtivo.sortBy} sortDir={filtroAtivo.sortDir} />
    </th>
  );

  const temMais = encomendas.length < totalEncomendas;

  return (
    <RelatorioEncomendasContainer>
      <MultiSelectGlobalStyle />
      <GlobalFilterStyle />

      {erro.isWrong && (
        <Modal
          visivel={erro.isWrong}
          titulo={erro.titulo}
          conteudo={erro.mensagem}
          autoCloseTime={8000}
          callbackModal={() => setErro({ isWrong: false, titulo: "", mensagem: "" })}
        />
      )}

      {isLoading && <FlexSpinner className="spinner" />}

      <RelatorioFiltrosForm>
        <form onSubmit={submitForm}>
          <div className="formulario-titulo">
            <h2>Filtros</h2>
          </div>

          <div className="formulario-linha">
            <div className="formulario-busca-input-area">
              <label htmlFor="dataInicio">Data início</label>
              <input
                type="date"
                id="dataInicio"
                className="input"
                value={request.dataInicio}
                onChange={(e) => setRequest((prev) => ({ ...prev, dataInicio: e.target.value }))}
              />
            </div>

            <div className="formulario-busca-input-area">
              <label htmlFor="dataFim">Data fim</label>
              <input
                type="date"
                id="dataFim"
                className="input"
                value={request.dataFim}
                onChange={(e) => setRequest((prev) => ({ ...prev, dataFim: e.target.value }))}
              />
            </div>

            <div className="formulario-busca-input-area">
              <label>Status</label>
              <MultiSelect
                value={request.status}
                options={STATUS_OPTIONS}
                onChange={(e) => setRequest((prev) => ({ ...prev, status: e.value }))}
                optionLabel="label"
                optionValue="value"
                placeholder="Todos"
                maxSelectedLabels={0}
                selectedItemsLabel="{0} selecionados"
                filter
                filterPlaceholder="Pesquisar"
                emptyMessage="Nenhum status encontrado"
                className="multiselect-custom"
                panelClassName="custom-filter-panel"
                style={{ width: "100%" }}
              />
            </div>

            <div className="formulario-busca-input-area">
              <label htmlFor="codigoEncomenda">Código de encomenda</label>
              <input
                type="text"
                id="codigoEncomenda"
                className="input"
                placeholder="Digite o código"
                value={request.codigosEncomenda[0] ?? ""}
                onChange={(e) =>
                  setRequest((prev) => ({
                    ...prev,
                    codigosEncomenda: e.target.value ? [e.target.value] : [],
                  }))
                }
              />
            </div>

            {isAdmin && (
              <div className="formulario-busca-input-area">
                <label htmlFor="userUuid">UUID do usuário</label>
                <input
                  type="text"
                  id="userUuid"
                  className="input"
                  placeholder="Filtrar por usuário"
                  value={request.userUuid}
                  onChange={(e) => setRequest((prev) => ({ ...prev, userUuid: e.target.value }))}
                />
              </div>
            )}
          </div>

          <div className="formulario-acoes">
            <button className="button buscar-button" type="submit">
              Buscar
            </button>
            <button
              type="button"
              className="button exportar-button"
              onClick={exportarCSV}
              disabled={isExportando}
            >
              {isExportando ? "Exportando..." : "Exportar CSV"}
            </button>
          </div>
        </form>
      </RelatorioFiltrosForm>

      <TabelaWrapper>
        <div className="tabela-cabecalho">
          <h2>
            Relatório de Encomendas{" "}
            {totalEncomendas > 0 && (
              <span style={{ fontWeight: "normal", fontSize: "0.9rem", color: "#888" }}>
                ({encomendas.length} de {totalEncomendas})
              </span>
            )}
          </h2>
          <div className="tabela-controles">
            <div className="select">
              <select
                value={filtroAtivo.size}
                onChange={(e) => handleSizeChange(Number(e.target.value))}
              >
                <option value="20">20 por página</option>
                <option value="50">50 por página</option>
                <option value="100">100 por página</option>
              </select>
            </div>
          </div>
        </div>

        {encomendas.length === 0 && !isLoading ? (
          <div className="tabela-vazia">Nenhum registro encontrado.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Transportadora</th>
                <th>Cód. encomenda</th>
                {thSortable("Data criação etiqueta", "dataCriacao")}
                <th>Data emissão</th>
                <th>Data prev. entrega</th>
                <th>Prazo</th>
                <th>Cidade</th>
                <th>UF</th>
                <th>Destinatário</th>
                <th>Telefone dest.</th>
                {thSortable("Status", "status")}
                <th>Data entrega</th>
                <th>CEP origem</th>
                <th>CEP destino</th>
                <th>Valor cobrado</th>
                <th>Compr. cotação</th>
                <th>Compr. emissão</th>
                <th>Larg. cotação</th>
                <th>Larg. emissão</th>
                <th>Alt. cotação</th>
                <th>Alt. emissão</th>
                <th>Peso cotação</th>
                <th>Peso emissão</th>
                <th>Num. Ped</th>
                <th>Data status</th>
                <th>Status completo</th>
                <th>Link rastreio</th>
              </tr>
            </thead>
            <tbody>
              {encomendas.map((item, idx) => (
                <tr key={item.id ? item.id : idx}>
                  <td>{nomePessoa(item.remetente)}</td>
                  <td>{item.transportadora || "-"}</td>
                  <td>{item.codigoEncomenda || "-"}</td>
                  <td>{formatDate(item.financeiro?.dataCriacaoEtiqueta)}</td>
                  <td>{formatDate(item.financeiro?.dataTransacao)}</td>
                  <td>{formatDate(item.financeiro?.dataPrevistaEntrega)}</td>
                  <td>{item.rastreamento?.prazoDias ?? "-"}</td>
                  <td>{item.destinatario?.cidade || "-"}</td>
                  <td>{item.destinatario?.estado || "-"}</td>
                  <td>{nomePessoa(item.destinatario)}</td>
                  <td>{item.destinatario?.telefone || "-"}</td>
                  <td>{item.status || "-"}</td>
                  <td>{formatDate(item.financeiro?.dataEntrega)}</td>
                  <td>{item.remetente?.cepOrigem || "-"}</td>
                  <td>{item.destinatario?.cepDestino || "-"}</td>
                  <td>{formatCurrency(item.financeiro?.valorCobradoCliente)}</td>
                  <td>{item.pacoteCotacao?.comprimento ?? "-"}</td>
                  <td>{item.pacoteEmissao?.comprimento ?? "-"}</td>
                  <td>{item.pacoteCotacao?.largura ?? "-"}</td>
                  <td>{item.pacoteEmissao?.largura ?? "-"}</td>
                  <td>{item.pacoteCotacao?.altura ?? "-"}</td>
                  <td>{item.pacoteEmissao?.altura ?? "-"}</td>
                  <td>{item.pacoteCotacao?.peso ?? "-"}</td>
                  <td>{item.pacoteEmissao?.peso ?? "-"}</td>
                  <td>{item.numeroPedido || "-"}</td>
                  <td>{formatDate(item.rastreamento?.dataOcorrencia)}</td>
                  <td>{item.rastreamento?.statusTransportadora || "-"}</td>
                  <td>
                    {item.linkRastreio ? (
                      <a href={item.linkRastreio} target="_blank" rel="noreferrer">
                        Rastrear
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {temMais && !isLoading && (
          <div className="tabela-rodape">
            <button className="button loadmore-button" type="button" onClick={carregarMais}>
              Carregar mais
            </button>
          </div>
        )}
      </TabelaWrapper>
    </RelatorioEncomendasContainer>
  );
}
